import {
  checkAuthCode,
  getAuthMethods,
  getAuthStatus,
  sendAuthCode,
} from "../api";
import { AuthChannel } from "../types";
import { AuthError, toAuthError } from "./auth-error";
import {
  AuthSession,
  createAuthSession,
  createRequestId,
  isSessionExpired,
  loadAuthSession,
  saveAuthSession,
  saveIfActive,
  upsertDelivery,
} from "./auth-session";
import { getWhiteLabelParams } from "./white-label";

/**
 * Сценарий входа OTP v3 поверх API: каждая функция обновляет сохранённую
 * сессию и бросает AuthError.
 */

const baseBody = (session: AuthSession) => ({
  phone: session.phone,
  auth_session_id: session.authSessionId,
  ...getWhiteLabelParams(),
});

/** Ошибка означает, что этот вход закончен и нужен новый. */
export const isSessionEndedError = (
  error: AuthError,
  session?: AuthSession | null,
) =>
  error.code === "INVALID_SESSION" ||
  error.code === "SESSION_CLOSED" ||
  (error.code === "EXPIRED" && (!session || isSessionExpired(session)));

/**
 * Начать вход по телефону. Если для этого телефона уже есть живая
 * сессия — продолжает её, иначе создаёт новую.
 */
export const startAuth = async (phone: number): Promise<AuthSession> => {
  const previous = loadAuthSession();
  const sessionId =
    previous?.phone === phone ? previous.authSessionId : undefined;

  const request = (authSessionId?: string) =>
    getAuthMethods({
      phone,
      ...(authSessionId ? { auth_session_id: authSessionId } : {}),
      ...getWhiteLabelParams(),
    });

  let response;
  try {
    response = await request(sessionId);
  } catch (error) {
    const authError = toAuthError(error);
    if (!sessionId || !isSessionEndedError(authError)) {
      throw authError;
    }
    // Старая сессия закрыта или истекла — начинаем новую.
    response = await request().catch((e) => {
      throw toAuthError(e);
    });
  }

  const session = createAuthSession({
    phone,
    authSessionId: response.auth_session_id,
    expiresAt: response.expires_at,
    channels: response.channels,
    previous,
  });
  saveAuthSession(session);
  return session;
};

/** Обновить список каналов текущей сессии (в т.ч. вернуть Telegram). */
export const refreshMethods = async (
  session: AuthSession,
): Promise<AuthSession> => {
  try {
    const response = await getAuthMethods(baseBody(session));
    const next: AuthSession = {
      ...session,
      authSessionId: response.auth_session_id,
      expiresAt: response.expires_at,
      channels: response.channels,
    };
    saveIfActive(session.authSessionId, next);
    return next;
  } catch (error) {
    throw toAuthError(error);
  }
};

/**
 * Отправить код. Новый request_id — на каждое действие пользователя;
 * после сетевой ошибки повтор того же канала идёт с прежним request_id.
 */
export const sendCode = async (
  session: AuthSession,
  channel: AuthChannel,
): Promise<AuthSession> => {
  const requestId =
    session.pendingRequest?.channel === channel
      ? session.pendingRequest.requestId
      : createRequestId();

  const pending: AuthSession = {
    ...session,
    pendingRequest: { channel, requestId },
  };
  saveIfActive(session.authSessionId, pending);

  try {
    const response = await sendAuthCode({
      ...baseBody(session),
      channel,
      request_id: requestId,
    });

    const next: AuthSession = {
      ...pending,
      pendingRequest: undefined,
      channels: response.channels,
      nextSendAt: response.next_send_at ?? pending.nextSendAt,
      deliveries: upsertDelivery(pending.deliveries, {
        request_id: response.request_id,
        channel,
        delivery_status: response.delivery_status,
        expires_at: response.expires_at,
      }),
    };
    saveIfActive(session.authSessionId, next);
    return next;
  } catch (error) {
    const authError = toAuthError(error);

    if (authError.code !== "NETWORK") {
      saveIfActive(session.authSessionId, {
        ...pending,
        pendingRequest: undefined,
        nextSendAt: authError.nextSendAt ?? pending.nextSendAt,
      });
    }

    throw authError;
  }
};

/** Опрос состояния отправок. */
export const pollStatus = async (
  session: AuthSession,
): Promise<AuthSession> => {
  try {
    const response = await getAuthStatus(baseBody(session));
    const current = loadAuthSession();
    if (!current || current.authSessionId !== session.authSessionId) {
      return session;
    }

    const deliveries = response.deliveries.reduce(
      (acc, delivery) => upsertDelivery(acc, delivery),
      current.deliveries,
    );

    const next: AuthSession = {
      ...current,
      expiresAt: response.expires_at,
      channels: response.channels,
      deliveries,
    };
    saveIfActive(session.authSessionId, next);
    return next;
  } catch (error) {
    throw toAuthError(error);
  }
};

/** Проверить код. Без автоматических повторов. */
export const checkCode = async (
  session: AuthSession,
  code: string,
): Promise<string> => {
  try {
    const response = await checkAuthCode({ ...baseBody(session), code });
    return response.accessToken;
  } catch (error) {
    throw toAuthError(error);
  }
};
